import { useQuery } from '@tanstack/react-query';

import { apiService } from '@/services/api-service';

/** Cross-association document search with facets (issue #404). */

export interface SearchFacetValue {
	value: string;
	count: number;
}

export interface SearchFacets {
	association: SearchFacetValue[];
	category: SearchFacetValue[];
	year: SearchFacetValue[];
	extension: SearchFacetValue[];
}

export interface PortfolioSearchItem {
	id: string;
	name: string;
	size?: number;
	modifiedAt?: string;
	extension: string;
	path: string[];
	association: string | null;
	category: string | null;
	year: string | null;
	/** D1 id of the owning association, when the hit's path maps to one. */
	associationId?: string;
}

export interface PortfolioSearchResult {
	query: string;
	/**
	 * More hits exist past this page — the honest replacement for a total count.
	 *
	 * Dropbox's `search_v2` reports no total, only `has_more` plus a cursor, so the old
	 * `approximateTotal` had nothing behind it to report and did not survive the move.
	 */
	hasMore: boolean;
	limit: number;
	items: PortfolioSearchItem[];
	facets: SearchFacets;
	/**
	 * What the caller must surface about how far these numbers can be trusted — written by
	 * the SERVER, which is the only side that knows what the provider actually did.
	 *
	 * They are not decoration. Facet counts cover the returned page rather than the archive,
	 * and content (full-text/OCR) search is plan-gated on Dropbox: on a plan without it this
	 * matches FILE NAMES only, which is a real capability loss versus the retired store. A
	 * searcher who is not told that concludes the document isn't there.
	 */
	caveats: string[];
	unavailable?: 'not-configured';
}

export type FacetSelection = {
	association: string[];
	category: string[];
	year: string[];
};

export const EMPTY_SELECTION: FacetSelection = { association: [], category: [], year: [] };

/**
 * Query key must be built from PRIMITIVES.
 *
 * The selection is an object of arrays — a new reference on every render — so it is
 * serialised to a stable string first. Passing it in raw would re-key each render and
 * turn the search into a refetch loop.
 */
const selectionKey = (s: FacetSelection): string => [
	s.association.slice().sort().join('|'),
	s.category.slice().sort().join('|'),
	s.year.slice().sort().join('|'),
].join('::');

export const searchAllKeys = {
	all: ['documentSearch'] as const,
	query: (q: string, selection: string) => [...searchAllKeys.all, q, selection] as const,
};

/**
 * Portfolio search. Pass an ALREADY-DEBOUNCED query.
 *
 * `placeholderData` keeps the previous results mounted while the next request is in
 * flight, so the list never blanks between keystrokes — the stable-search rule in
 * CLAUDE.md.
 */
export function usePortfolioSearch(query: string, selection: FacetSelection = EMPTY_SELECTION) {
	const trimmed = query.trim();
	const params = new URLSearchParams({ q: trimmed, limit: '200' });
	for (const value of selection.association) params.append('association', value);
	for (const value of selection.category) params.append('category', value);
	for (const value of selection.year) params.append('year', value);

	return useQuery<PortfolioSearchResult>({
		queryKey: searchAllKeys.query(trimmed, selectionKey(selection)),
		queryFn: () => apiService.fetchApi(
			`/documents/search?${params.toString()}`,
		) as Promise<PortfolioSearchResult>,
		enabled: trimmed.length > 0,
		placeholderData: (previous) => previous,
		staleTime: 60_000,
		retry: false,
	});
}
